import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Phone, MessageSquare, Lock, ArrowRight } from 'lucide-react';
import { resetPasswordApi, sendSmsCodeApi } from '../api/auth';
import useSmsCountdown from '../hooks/useSmsCountdown';
import Toast from '../components/Toast/Toast';
import '../styles/auth.scss';

function ForgotPassword() {
  const navigate = useNavigate();
  const { countdown, start, isCounting } = useSmsCountdown('reset');
  const [form, setForm] = useState({
    phone: '',
    smsCode: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState({ visible: false, message: '' });

  const onChange = (key, value) => {
    setForm(prev => ({ ...prev, [key]: value }));
    setErrors(prev => ({ ...prev, [key]: '' }));
  };

  const validate = () => {
    const errs = {};
    if (!form.phone.trim()) errs.phone = '请输入手机号';
    else if (!/^1[3-9]\d{9}$/.test(form.phone)) errs.phone = '手机号格式不正确';
    if (!form.smsCode.trim()) errs.smsCode = '请输入验证码';
    else if (!/^\d{4,6}$/.test(form.smsCode)) errs.smsCode = '验证码格式不正确';
    if (!form.newPassword) errs.newPassword = '请输入新密码';
    else if (form.newPassword.length < 6) errs.newPassword = '密码至少6位';
    if (!form.confirmPassword) errs.confirmPassword = '请确认新密码';
    else if (form.newPassword !== form.confirmPassword) errs.confirmPassword = '两次密码不一致';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSendSms = async () => {
    if (isCounting) return;
    if (!form.phone.trim()) {
      setErrors(prev => ({ ...prev, phone: '请先输入手机号' }));
      return;
    }
    if (!/^1[3-9]\d{9}$/.test(form.phone)) {
      setErrors(prev => ({ ...prev, phone: '手机号格式不正确' }));
      return;
    }
    try {
      await sendSmsCodeApi({ phone: form.phone, type: 'reset' });
      start();
    } catch (err) {
      setToast({ visible: true, message: err.message || '发送失败' });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      await resetPasswordApi({
        phone: form.phone,
        smsCode: form.smsCode,
        newPassword: form.newPassword
      });
      // TODO: 重置成功后跳转登录
      navigate('/login');
    } catch (err) {
      setToast({ visible: true, message: err.message || '重置失败' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <Toast visible={toast.visible} message={toast.message} onClose={() => setToast({ visible: false, message: '' })} />
      <span className="auth-badge">SECURE AUTHENTICATION</span>
      <h1 className="auth-title">找回密码</h1>
      <p className="auth-desc">通过手机短信验证码重置您的密码。</p>

      <form className="auth-card" onSubmit={handleSubmit}>
        <div className="auth-field">
          <label className="auth-field-label">手机号 (PHONE NUMBER)</label>
          <div className="auth-field-input-wrap">
            <Phone size={18} className="field-icon" />
            <input
              type="tel"
              placeholder="请输入您的手机号"
              value={form.phone}
              onChange={e => onChange('phone', e.target.value)}
            />
          </div>
          {errors.phone && <div className="auth-field-error">{errors.phone}</div>}
        </div>

        <div className="auth-field">
          <label className="auth-field-label">验证码 (SMS CODE)</label>
          <div className="auth-field-input-wrap">
            <MessageSquare size={18} className="field-icon" />
            <input
              type="text"
              placeholder="请输入短信验证码"
              maxLength={6}
              value={form.smsCode}
              onChange={e => onChange('smsCode', e.target.value)}
            />
            <button
              type="button"
              className="sms-btn"
              disabled={isCounting}
              onClick={handleSendSms}
            >
              {isCounting ? `${countdown}s` : '获取验证码'}
            </button>
          </div>
          {errors.smsCode && <div className="auth-field-error">{errors.smsCode}</div>}
        </div>

        <div className="auth-field">
          <label className="auth-field-label">新密码 (NEW PASSWORD)</label>
          <div className="auth-field-input-wrap">
            <Lock size={18} className="field-icon" />
            <input
              type="password"
              placeholder="请输入新密码（至少6位）"
              value={form.newPassword}
              onChange={e => onChange('newPassword', e.target.value)}
            />
          </div>
          {errors.newPassword && <div className="auth-field-error">{errors.newPassword}</div>}
        </div>

        <div className="auth-field">
          <label className="auth-field-label">确认新密码 (CONFIRM PASSWORD)</label>
          <div className="auth-field-input-wrap">
            <Lock size={18} className="field-icon" />
            <input
              type="password"
              placeholder="请再次输入新密码"
              value={form.confirmPassword}
              onChange={e => onChange('confirmPassword', e.target.value)}
            />
          </div>
          {errors.confirmPassword && <div className="auth-field-error">{errors.confirmPassword}</div>}
        </div>

        <button className="auth-submit" type="submit" disabled={loading}>
          {loading ? '提交中...' : '重置密码'} <ArrowRight size={18} />
        </button>

        <Link className="auth-link" to="/login">返回登录</Link>
      </form>
    </div>
  );
}

export default ForgotPassword;
